<script lang="ts">
  /**
   * ソリューション詳細「営業支援」タブの「資料」セクション。
   *
   * 資料は `product_sales_materials` を正本にしつつ、UI は 利用場面 (任意自由記述) /
   * title / URL のインライン編集テーブルに絞る。末尾には常に入力用の空欄行を 1 行だけ置く。
   */
  import { productSalesMaterialsStore } from "@entities/product/sales-materials.store.svelte";
  import {
    buildCreatePayload,
    buildPatchPayload,
    draftFromMaterial,
    formatError,
    isBlankDraft,
    isCompleteDraft,
    nextSortOrder,
    validateDraft,
  } from "@domain/product/sales-material-draft";
  import type { MaterialDraft } from "@domain/product/sales-material-draft";
  import type { ProductSalesMaterial } from "@shared/types/product-sales-material";
  import { createUrl } from "@shared/value-objects";

  interface Props {
    productId: string;
    canEdit?: boolean;
  }

  interface MaterialTableRow extends MaterialDraft {
    key: string;
    materialId: string | null;
    source: ProductSalesMaterial | null;
  }

  const { productId, canEdit = false }: Props = $props();

  let rows = $state<MaterialTableRow[]>([]);
  let busyKeys = $state<Set<string>>(new Set());
  let localError = $state<string | null>(null);
  let blankSequence = 0;

  const errorMessage = $derived(localError ?? productSalesMaterialsStore.error);

  $effect(() => {
    if (!productId) return;
    void productSalesMaterialsStore.load(productId);
    return () => {
      productSalesMaterialsStore.clear();
    };
  });

  $effect(() => {
    rows = rowsFromMaterials(productSalesMaterialsStore.data);
  });

  function rowsFromMaterials(
    materials: ProductSalesMaterial[],
  ): MaterialTableRow[] {
    const materialRows = materials.map((material) => ({
      key: material.id,
      materialId: material.id,
      source: material,
      ...draftFromMaterial(material),
    }));
    return canEdit ? [...materialRows, createBlankRow()] : materialRows;
  }

  function createBlankRow(): MaterialTableRow {
    blankSequence += 1;
    return {
      key: `blank-${blankSequence}`,
      materialId: null,
      source: null,
      title: "",
      url: "",
      usage_scene: "",
    };
  }

  function normalizeRows(nextRows: MaterialTableRow[]): MaterialTableRow[] {
    if (!canEdit) return nextRows;
    const contentRows = nextRows.filter(
      (row) => row.materialId !== null || !isBlankDraft(row),
    );
    return [...contentRows, createBlankRow()];
  }

  function setBusy(key: string, busy: boolean): void {
    const next = new Set(busyKeys);
    if (busy) next.add(key);
    else next.delete(key);
    busyKeys = next;
  }

  function updateRowField(
    key: string,
    field: keyof MaterialDraft,
    value: string,
  ): void {
    if (!canEdit) return;
    localError = null;
    rows = normalizeRows(
      rows.map((row) => (row.key === key ? { ...row, [field]: value } : row)),
    );
  }

  function currentMaterialFor(
    row: MaterialTableRow,
  ): ProductSalesMaterial | null {
    if (!row.materialId) return null;
    return (
      productSalesMaterialsStore.data.find(
        (material) => material.id === row.materialId,
      ) ?? row.source
    );
  }

  function trimRowInPlace(key: string, draft: MaterialDraft): void {
    rows = rows.map((row) =>
      row.key === key
        ? { ...row, title: draft.title.trim(), url: draft.url.trim() }
        : row,
    );
  }

  async function commitRow(key: string): Promise<void> {
    if (!canEdit || busyKeys.has(key)) return;
    const row = rows.find((candidate) => candidate.key === key);
    if (!row) return;

    const validationMessage = validateDraft(row);
    if (validationMessage) {
      localError = validationMessage;
      return;
    }
    if (!isCompleteDraft(row)) {
      localError = null;
      return;
    }

    const draft: MaterialDraft = {
      title: row.title.trim(),
      url: row.url.trim(),
      usage_scene: row.usage_scene.trim(),
    };
    trimRowInPlace(key, draft);

    setBusy(key, true);
    localError = null;
    try {
      if (row.materialId) {
        const current = currentMaterialFor(row);
        if (!current) return;
        const patch = buildPatchPayload(current, draft);
        if (Object.keys(patch).length === 0) return;
        await productSalesMaterialsStore.update(
          productId,
          row.materialId,
          patch,
        );
      } else {
        await productSalesMaterialsStore.create(
          productId,
          buildCreatePayload(
            draft,
            nextSortOrder(productSalesMaterialsStore.data),
          ),
        );
      }
    } catch (e) {
      localError = formatError(e, "保存に失敗しました");
    } finally {
      setBusy(key, false);
    }
  }

  function handleEnter(event: KeyboardEvent, key: string): void {
    if (event.key !== "Enter") return;
    event.preventDefault();
    void commitRow(key);
  }
</script>

<section
  class="sales-materials-editor"
  data-testid="product-sales-materials-editor"
  aria-busy={productSalesMaterialsStore.loading || busyKeys.size > 0}
>
  {#if productSalesMaterialsStore.loading}
    <p class="muted small">読み込み中…</p>
  {/if}

  {#if errorMessage}
    <p class="error" role="alert" data-testid="product-sales-materials-error">
      {errorMessage}
    </p>
  {/if}

  <div class="materials-table-wrap" data-testid="product-sales-materials-list">
    <table class="materials-table">
      <colgroup>
        <col class="usage-col" />
        <col class="title-col" />
        <col class="url-col" />
      </colgroup>
      <thead>
        <tr>
          <th>利用場面</th>
          <th>タイトル</th>
          <th>URL</th>
        </tr>
      </thead>
      <tbody>
        {#if rows.length === 0 && !productSalesMaterialsStore.loading}
          <tr>
            <td
              class="empty-cell"
              colspan="3"
              data-testid="product-sales-materials-empty"
            >
              資料はまだありません。
            </td>
          </tr>
        {:else}
          {#each rows as row (row.key)}
            {@const rowBusy = busyKeys.has(row.key)}
            {@const savedHref = createUrl(row.source?.url ?? "")}
            <tr
              class:input-row={row.materialId === null}
              class:busy={rowBusy}
              data-testid="product-sales-materials-row"
              data-material-id={row.materialId ?? ""}
            >
              <td>
                {#if canEdit}
                  <input
                    type="text"
                    aria-label="利用場面"
                    data-testid="product-sales-materials-usage-input"
                    value={row.usage_scene}
                    placeholder={row.materialId === null
                      ? "例: 初回商談"
                      : undefined}
                    disabled={rowBusy}
                    oninput={(event) =>
                      updateRowField(
                        row.key,
                        "usage_scene",
                        (event.currentTarget as HTMLInputElement)
                          .value,
                      )}
                    onblur={() => void commitRow(row.key)}
                    onkeydown={(event) => handleEnter(event, row.key)}
                  />
                {:else}
                  <span
                    class="cell-text"
                    data-testid="product-sales-materials-usage-text"
                  >
                    {row.usage_scene || "-"}
                  </span>
                {/if}
              </td>
              <td>
                {#if canEdit}
                  <input
                    type="text"
                    aria-label="資料タイトル"
                    data-testid="product-sales-materials-title-input"
                    value={row.title}
                    placeholder={row.materialId === null
                      ? "資料タイトル"
                      : undefined}
                    disabled={rowBusy}
                    oninput={(event) =>
                      updateRowField(
                        row.key,
                        "title",
                        (event.currentTarget as HTMLInputElement).value,
                      )}
                    onblur={() => void commitRow(row.key)}
                    onkeydown={(event) => handleEnter(event, row.key)}
                  />
                {:else if savedHref}
                  <a
                    class="cell-link title-link"
                    href={savedHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={row.url}
                    data-testid="product-sales-materials-title-link"
                  >
                    {row.title}
                  </a>
                {:else}
                  <span
                    class="cell-text"
                    title={row.url}
                    data-testid="product-sales-materials-title-text"
                  >
                    {row.title}
                  </span>
                {/if}
              </td>
              <td>
                {#if canEdit}
                  <div class="url-cell">
                    <input
                      type="url"
                      aria-label="資料URL"
                      data-testid="product-sales-materials-url-input"
                      value={row.url}
                      placeholder={row.materialId === null
                        ? "https://..."
                        : undefined}
                      disabled={rowBusy}
                      oninput={(event) =>
                        updateRowField(
                          row.key,
                          "url",
                          (event.currentTarget as HTMLInputElement).value,
                        )}
                      onblur={() => void commitRow(row.key)}
                      onkeydown={(event) => handleEnter(event, row.key)}
                    />
                    {#if savedHref}
                      <a
                        class="open-url-link"
                        href={savedHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="資料URLを開く"
                        data-testid="product-sales-materials-url-open"
                      >
                        ↗
                      </a>
                    {/if}
                  </div>
                {:else if savedHref}
                  <a
                    class="cell-link url-link"
                    href={savedHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={row.url}
                    data-testid="product-sales-materials-url-link"
                  >
                    {row.url}
                  </a>
                {:else}
                  <span
                    class="cell-text"
                    title={row.url}
                    data-testid="product-sales-materials-url-text"
                  >
                    {row.url}
                  </span>
                {/if}
              </td>
            </tr>
          {/each}
        {/if}
      </tbody>
    </table>
  </div>
</section>

<style>
  .sales-materials-editor {
    display: grid;
    gap: 10px;
    min-width: 0;
  }

  .materials-table-wrap {
    width: 100%;
    overflow-x: auto;
    border: 1px solid #dbe3ef;
    border-radius: 8px;
    background: #fff;
  }

  .materials-table {
    width: 100%;
    min-width: 460px;
    border-collapse: collapse;
    table-layout: fixed;
  }

  .usage-col {
    width: 15%;
  }

  .title-col {
    width: 70%;
  }

  .url-col {
    width: 15%;
  }

  th,
  td {
    border-bottom: 1px solid #e5edf6;
    border-right: 1px solid #e5edf6;
    padding: 0;
    text-align: left;
    vertical-align: middle;
  }

  th:last-child,
  td:last-child {
    border-right: 0;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  th {
    background: #f8fafc;
    color: #475569;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.4;
    padding: 8px 10px;
  }

  tr.busy {
    opacity: 0.7;
  }

  input {
    width: 100%;
    min-width: 0;
    min-height: 36px;
    border: 0;
    border-radius: 0;
    background: transparent;
    color: #0f172a;
    font: inherit;
    font-size: 13px;
    line-height: 1.4;
    padding: 8px 10px;
  }

  input::placeholder {
    color: #94a3b8;
  }

  input:focus {
    position: relative;
    z-index: 1;
    outline: 2px solid #2563eb;
    outline-offset: -2px;
    background: #fff;
  }

  input:disabled {
    color: #334155;
    opacity: 1;
    -webkit-text-fill-color: #334155;
  }

  .url-cell {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .url-cell input {
    flex: 1 1 auto;
    min-width: 0;
  }

  .open-url-link {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    margin-right: 6px;
    border-radius: 4px;
    color: #1d4ed8;
    font-size: 13px;
    text-decoration: none;
  }

  .open-url-link:hover {
    background: #eff6ff;
  }

  .cell-text {
    display: block;
    min-width: 0;
    overflow: hidden;
    color: #0f172a;
    font-size: 13px;
    line-height: 1.4;
    padding: 8px 10px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cell-link {
    display: block;
    min-width: 0;
    overflow: hidden;
    color: #1d4ed8;
    font-size: 13px;
    line-height: 1.4;
    padding: 8px 10px;
    text-decoration: none;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cell-link:hover {
    text-decoration: underline;
  }

  .title-link {
    font-weight: 700;
  }

  .input-row input {
    color: #0f172a;
  }

  .empty-cell {
    color: #64748b;
    font-size: 13px;
    padding: 14px 10px;
  }

  .muted {
    margin: 0;
    color: #6b7280;
  }

  .small {
    font-size: 12px;
  }

  .error {
    margin: 0;
    color: #991b1b;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 700;
  }

  @media (max-width: 640px) {
    .materials-table {
      min-width: 420px;
    }

    input {
      min-height: 40px;
      font-size: 13px;
      padding: 9px 8px;
    }
  }
</style>
